import * as React from "react";
import { TinyColor } from "@ctrl/tinycolor";
import {
	makeStyles,
	Button,
	Popover,
	PopoverSurface,
	PopoverTrigger,
} from "@fluentui/react-components";
import {
	ColorPicker,
	ColorSlider,
	AlphaSlider,
	ColorArea,
} from "@fluentui/react-color-picker-preview";

// Definizione degli stili con makeStyles di Fluent UI
const useStyles = makeStyles({
	previewColor: {
		margin: "10px 0",
		width: "50px",
		height: "50px",
		borderRadius: "4px",
		border: "1px solid #ccc",
		"@media (forced-colors: active)": {
			forcedColorAdjust: "none",
		},
	},
	row: {
		display: "flex",
		gap: "10px",
		alignItems: "center",
	},
	sliders: {
		display: "flex",
		flexDirection: "column",
		gap: "4px",
	},
});

// Valore predefinito in formato HSV
const DEFAULT_COLOR_HSV = { h: 109, s: 1, v: 0.9, a: 1 };

/**
 * ColorPickerPopup
 *
 * @param {object} props
 * @param {object} [props.initialColor] - Valore iniziale del colore (in formato HSV)
 * @param {function} props.onColorSelected - Callback che riceve il colore selezionato (in formato RGB string)
 */
const ColorPickerPopup = ({
	initialColor = DEFAULT_COLOR_HSV,
	onColorSelected,
}) => {
	const styles = useStyles();
	const [previewColor, setPreviewColor] = React.useState(initialColor);
	const [confirmedColor, setConfirmedColor] = React.useState(initialColor);
	const [popoverOpen, setPopoverOpen] = React.useState(false);

	// Gestore della modifica del colore: aggiorna il colore in anteprima
	const handleChange = (_, data) => {
		setPreviewColor({ ...data.color, a: data.color.a ?? 1 });
	};

	// Quando l'utente clicca "Ok" si salva il colore confermato e si richiama la callback
	const handleOk = () => {
		setConfirmedColor(previewColor);
		setPopoverOpen(false);
		if (onColorSelected) {
			// Convertiamo il colore in una stringa RGB (es. "rgb(123, 45, 67)")
			onColorSelected(new TinyColor(previewColor).toRgbString());
		}
	};

	return (
		<>
			<Popover
				open={popoverOpen}
				trapFocus
				onOpenChange={(_, data) => setPopoverOpen(data.open)}
			>
				<PopoverTrigger disableButtonEnhancement>
					<Button onClick={() => setPopoverOpen(true)}>Choose color</Button>
				</PopoverTrigger>

				<PopoverSurface>
					<ColorPicker color={previewColor} onColorChange={handleChange}>
						<ColorArea
							inputX={{ "aria-label": "Saturation" }}
							inputY={{ "aria-label": "Brightness" }}
						/>
						<div className={styles.row}>
							<div className={styles.sliders}>
								<ColorSlider aria-label="Hue" />
								<AlphaSlider aria-label="Alpha" />
							</div>
							<div
								className={styles.previewColor}
								style={{
									backgroundColor: new TinyColor(previewColor).toRgbString(),
								}}
							/>
						</div>
					</ColorPicker>
					<div className={styles.row}>
						<Button appearance="primary" onClick={handleOk}>
							Ok
						</Button>
						<Button onClick={() => setPopoverOpen(false)}>Cancel</Button>
					</div>
				</PopoverSurface>
			</Popover>
			{/* Anteprima del colore confermato */}
			<div
				className={styles.previewColor}
				style={{ backgroundColor: new TinyColor(confirmedColor).toRgbString() }}
			/>
		</>
	);
};

export default ColorPickerPopup;
